import { AdminPagination as ZhPagination } from "@/views/components/AdminPagination";
import { Alert, Button, Input, Space } from "antd";
import type { AxiosResponse } from "axios";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";

import request from "@/api";
import type { CustomAxiosRequestConfig } from "@/api";
import { LOG_DATE_FORMAT_END, LOG_DATE_FORMAT_START } from "@/config";
import { dateFormat } from "@/utils";
import { fmtMoney } from "@/utils/money";
import { useUserStore } from "@/views/stores";
import { DateRangePicker as ZhDateRangePicker, Table as ZhTable, type TableColumnProps } from "zhihao-ui-react";
import {
    ZoneRechargeFormDialog,
    type RechargeAccountInfo,
    type RechargeCustomerOption,
    type RechargeZoneOption,
    type ZoneRechargePayload
} from "./ZoneRechargeFormDialog";

export interface ZoneRechargeRow {
    recharge?: {
        id?: string | number;
        createdTime?: string;
        beforeBalanceAmount?: number;
        rechargeAmount?: number;
        afterBalanceAmount?: number;
        snapCustomerInfo?: { realName?: string; customerCode?: string; mobile?: string; institutionName?: string };
    };
    specialZoneName?: string;
    creator?: string;
}

export interface ZoneRechargePageResult {
    list?: ZoneRechargeRow[];
    total?: number;
}

export interface ZoneRechargeQuery {
    page: number;
    size: number;
    rechargeId?: string;
    keyword?: string;
    creator?: string;
    startDate?: string;
    endDate?: string;
    accountId?: string | number;
}

export interface ZoneRechargePageProps {
    exportRecharges?: (query: ZoneRechargeQuery) => Promise<void>;
    loadRecharges?: (query: ZoneRechargeQuery) => Promise<ZoneRechargePageResult>;
    permissions?: string[];
    accountId?: string | number;
    searchCustomers?: (keyword: string) => Promise<RechargeCustomerOption[]>;
    loadZones?: (params: { customerId: number; zoneName: string }) => Promise<RechargeZoneOption[]>;
    findAccount?: (params: { customerId: number; zoneId: number }) => Promise<RechargeAccountInfo>;
    submitRecharge?: (payload: ZoneRechargePayload) => Promise<unknown>;
}

const defaultQuery: ZoneRechargeQuery = { page: 1, size: 10 };

/** 复用 Vue 专区充值明细分页接口。 */
export async function loadZoneRecharges(query: ZoneRechargeQuery): Promise<ZoneRechargePageResult> {
    const response = await request.service.get<ZoneRechargePageResult>("/admin/special-zone-recharge/paging", {
        params: query
    });
    return response.data;
}

/** 从 Vue 导出响应头读取文件名，缺失或编码异常时回退到稳定的中文文件名。 */
function getZoneRechargeExportFilename(contentDisposition?: string): string {
    const fallback = "专区充值明细.xlsx";
    if (!contentDisposition) return fallback;

    const encodedFilename = contentDisposition.match(/filename\*=(?:UTF-8'')?([^;]+)/i)?.[1]?.trim();
    const filename = encodedFilename || contentDisposition.match(/filename=([^;]+)/i)?.[1]?.trim();
    if (!filename) return fallback;

    const normalizedFilename = filename.replace(/^['"]|['"]$/g, "");
    try {
        return decodeURIComponent(normalizedFilename) || fallback;
    } catch {
        return normalizedFilename || fallback;
    }
}

/** 将 Vue 返回的 Blob 转换为下载，并保证临时锚点和 Object URL 在完成后清理。 */
function downloadZoneRechargeBlob(blob: Blob, filename: string): void {
    const objectUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = objectUrl;
    link.setAttribute("download", filename);
    document.body.appendChild(link);

    try {
        link.click();
    } finally {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(objectUrl);
    }
}

/** 复用 Vue 专区充值明细导出接口，保持筛选参数和原始 Blob 响应契约。 */
export async function exportZoneRecharges(query: ZoneRechargeQuery): Promise<void> {
    // Vue 导出接口使用完整起止时间，列表查询保留 startDate/endDate 字段。
    const { startDate, endDate, ...restQuery } = query;
    const exportQuery = {
        ...restQuery,
        ...(startDate ? { beginTime: dateFormat(startDate, LOG_DATE_FORMAT_START) } : {}),
        ...(endDate ? { endTime: dateFormat(endDate, LOG_DATE_FORMAT_END) } : {})
    };
    const response = await request.service.get<Blob, AxiosResponse<Blob>>("/admin/special-zone-recharge/export", {
        params: exportQuery,
        responseType: "blob",
        returnRawResponse: true
    } as CustomAxiosRequestConfig);
    const filename = getZoneRechargeExportFilename(response.headers["content-disposition"]);
    downloadZoneRechargeBlob(response.data, filename);
}

/** 将 Vue 快照主体字段转换为原页面展示格式。 */
function getCustomerLabel(row: ZoneRechargeRow): string {
    const customer = row.recharge?.snapCustomerInfo;
    return [customer?.realName, customer?.customerCode].filter(Boolean).join(" | ") || "--";
}

/** 专区充值明细列表，保留 Vue 筛选字段、金额汇总和管理入口。 */
export function ZoneRechargePage({
    exportRecharges = exportZoneRecharges,
    loadRecharges = loadZoneRecharges,
    permissions,
    accountId,
    searchCustomers,
    loadZones,
    findAccount,
    submitRecharge
}: ZoneRechargePageProps) {
    const navigate = useNavigate();
    const storePermissions = useUserStore(state => state.userInfo.permissions || []);
    const currentPermissions = permissions ?? storePermissions;
    const canManage = currentPermissions.includes("ROLE_ZONE_ACCOUNT_MANAGE");
    const [rows, setRows] = useState<ZoneRechargeRow[]>([]);
    const [total, setTotal] = useState(0);
    const [query, setQuery] = useState<ZoneRechargeQuery>(() => ({
        ...defaultQuery,
        ...(accountId == null ? {} : { keyword: String(accountId) })
    }));
    const [rechargeId, setRechargeId] = useState("");
    // 账户查询跳转时沿用 Vue 充值明细的 keyword 筛选，避免丢失主体条件。
    const [keyword, setKeyword] = useState(() => (accountId == null ? "" : String(accountId)));
    const [creator, setCreator] = useState("");
    // 对齐 Vue 充值时间筛选，查询接口使用 startDate/endDate。
    const [createdDateRange, setCreatedDateRange] = useState<[string | null, string | null]>([null, null]);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [exporting, setExporting] = useState(false);
    const [rechargeVisible, setRechargeVisible] = useState(false);

    useEffect(() => {
        let disposed = false;
        void loadRecharges(query)
            .then(result => {
                if (disposed) return;
                setRows(result.list || []);
                setTotal(result.total || 0);
                setErrorMessage(null);
            })
            .catch(() => {
                if (!disposed) setErrorMessage("专区充值明细加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [loadRecharges, query]);

    const search = () => {
        setQuery({
            ...defaultQuery,
            ...(accountId == null ? {} : { keyword: String(accountId) }),
            ...(rechargeId.trim() ? { rechargeId: rechargeId.trim() } : {}),
            ...(keyword.trim() ? { keyword: keyword.trim() } : {}),
            ...(creator.trim() ? { creator: creator.trim() } : {}),
            ...(createdDateRange[0] ? { startDate: createdDateRange[0] } : {}),
            ...(createdDateRange[1] ? { endDate: createdDateRange[1] } : {})
        });
    };

    /** 使用页面已提交的查询条件导出，失败时保留可见的业务反馈。 */
    const handleExport = async () => {
        setExporting(true);
        setErrorMessage(null);
        try {
            await exportRecharges(query);
        } catch {
            setErrorMessage("专区充值明细导出失败");
        } finally {
            setExporting(false);
        }
    };

    const totalAmount = rows.reduce((sum, row) => sum + Number(row.recharge?.rechargeAmount || 0), 0);

    return (
        <section aria-label="zone-recharge-page">
            <ZhTable
                border
                showSummary
                sumText="合计"
                summaryMethod={({ columns, data }) =>
                    columns.map((column, index) => {
                        if (index === 0) return "合计";
                        if (column.prop === "amount") {
                            return data
                                .reduce((sum, item) => sum + Number(item.recharge?.rechargeAmount ?? 0), 0)
                                .toFixed(2);
                        }
                        return "";
                    })
                }
                columns={
                    [
                        {
                            label: "充值时间",
                            minWidth: 154,
                            prop: "createdTime",
                            render: ({ row }) => row.recharge?.createdTime?.slice(0, 16) || "--"
                        },
                        {
                            label: "充值编号",
                            minWidth: 176,
                            prop: "rechargeId",
                            render: ({ row }) =>
                                row.recharge?.id ? (
                                    <Button
                                        type="link"
                                        onClick={() => navigate(`/tradeManage/rechargeDetails/${row.recharge?.id}`)}
                                    >
                                        {row.recharge.id}
                                    </Button>
                                ) : (
                                    "--"
                                )
                        },
                        {
                            label: "充值专区",
                            minWidth: 176,
                            prop: "zone",
                            render: ({ row }) => row.specialZoneName || "--"
                        },
                        {
                            label: "个人实名/用户ID",
                            minWidth: 176,
                            prop: "customer",
                            render: ({ row }) => getCustomerLabel(row)
                        },
                        {
                            label: "手机号",
                            width: 176,
                            prop: "mobile",
                            render: ({ row }) => row.recharge?.snapCustomerInfo?.mobile || "--"
                        },
                        {
                            label: "机构名称",
                            minWidth: 176,
                            prop: "institution",
                            render: ({ row }) => row.recharge?.snapCustomerInfo?.institutionName || "--"
                        },
                        {
                            label: "充值前余额（元）",
                            width: 176,
                            prop: "beforeBalance",
                            render: ({ row }) => fmtMoney(row.recharge?.beforeBalanceAmount)
                        },
                        {
                            label: "充值金额（元）",
                            width: 176,
                            prop: "amount",
                            render: ({ row }) => fmtMoney(row.recharge?.rechargeAmount)
                        },
                        {
                            label: "充值后余额（元）",
                            width: 176,
                            prop: "afterBalance",
                            render: ({ row }) => fmtMoney(row.recharge?.afterBalanceAmount)
                        },
                        { label: "操作人",
                        fixed: "right", width: 240, prop: "creator", render: ({ row }) => row.creator || "--" }
                    ] as TableColumnProps<ZoneRechargeRow>[]
                }
                data={rows}
                emptyText="暂无充值明细"
                pagination={false}
                rowKey={row => String(row.recharge?.id)}
                isShowResetBtn={false}
                isShowSearchBtn={false}
                tableHeader={
                    <div className="admin-table-composed-header">
                        {errorMessage ? (
                            <Alert className="mb-4" message={errorMessage} role="alert" type="error" />
                        ) : null}

                        <Space className="admin-table-filter-form mb-4" wrap>
                            <Input
                                aria-label="充值编号"
                                placeholder="请输入充值编号搜索"
                                value={rechargeId}
                                onChange={event => setRechargeId(event.target.value)}
                            />
                            <Input
                                aria-label="用户关键词"
                                placeholder="请输入用户ID/个人或机构实名/手机号搜索"
                                value={keyword}
                                onChange={event => setKeyword(event.target.value)}
                            />
                            <Input
                                aria-label="操作人"
                                placeholder="请输入操作人搜索"
                                value={creator}
                                onChange={event => setCreator(event.target.value)}
                            />
                            <label>
                                充值日期
                                <ZhDateRangePicker
                                    type="daterange"
                                    format="YYYY-MM-DD"
                                    valueFormat="YYYY-MM-DD"
                                    rangeSeparator="-"
                                    startPlaceholder="充值开始日期"
                                    endPlaceholder="充值结束日期"
                                    value={createdDateRange}
                                    onChange={(_value, formattedValue) =>
                                        setCreatedDateRange(
                                            formattedValue
                                                ? [formattedValue[0] || null, formattedValue[1] || null]
                                                : [null, null]
                                        )
                                    }
                                />
                            </label>
                            <Button aria-label="查询充值" type="primary" onClick={search}>
                                查询
                            </Button>
                            {canManage ? (
                                <Button aria-label="充值" onClick={() => setRechargeVisible(true)}>
                                    充值
                                </Button>
                            ) : null}
                            {canManage ? (
                                <Button aria-label="导出" loading={exporting} onClick={() => void handleExport()}>
                                    导出
                                </Button>
                            ) : null}
                        </Space>

                        <div className="mb-2">充值合计：{fmtMoney(totalAmount)}</div>
                    </div>
                }
                append={
                    <div className="admin-table-composed-pagination">
                        <ZhPagination
                            className="mt-4 self-end"
                            current={query.page}
                            pageSize={query.size}
                            total={total}
                            onChange={(page, size) => setQuery(current => ({ ...current, page, size }))}
                        />
                    </div>
                }
            />
            <ZoneRechargeFormDialog
                findAccount={findAccount}
                loadZones={loadZones}
                onCancel={() => setRechargeVisible(false)}
                onSuccess={() => setQuery({ ...query })}
                open={rechargeVisible}
                searchCustomers={searchCustomers}
                submitRecharge={submitRecharge}
            />
        </section>
    );
}

export default ZoneRechargePage;
